/**
 * SMTP Configuration Component
 * Allows administrators to configure email settings and test SMTP connection
 */

import React, { useState, useEffect } from 'react';
import { Mail, Send, Check, X, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { apiClient } from '../../services/api';

interface SMTPSettings {
  host: string;
  port: number;
  username: string;
  password: string;
  use_tls: boolean;
  use_ssl: boolean;
  skip_ssl_verification: boolean;
  from_email: string;
  from_name: string;
}

interface TestEmailData {
  recipients: string;
  subject: string;
  message: string;
}

const SMTPConfiguration: React.FC = () => {
  const [settings, setSettings] = useState<SMTPSettings>({
    host: '',
    port: 587,
    username: '',
    password: '',
    use_tls: true,
    use_ssl: false,
    skip_ssl_verification: false,
    from_email: '',
    from_name: 'KATA Lean 4.0'
  });

  const [testEmail, setTestEmail] = useState<TestEmailData>({
    recipients: '',
    subject: 'KATA Lean 4.0 Test Email',
    message: 'This is a test email from KATA Lean 4.0 SMTP configuration.'
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [showTestForm, setShowTestForm] = useState(false);

  // Load existing settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/settings/smtp/');
      // Don't load the masked password, keep it empty for user input
      const { password, ...otherSettings } = response.data;
      setSettings(prev => ({ ...prev, ...otherSettings, password: '' }));
    } catch (error) {
      console.error('Failed to load SMTP settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingChange = (field: keyof SMTPSettings, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    // Clear any existing messages when settings change
    if (message) setMessage(null);
  };

  const handleTestEmailChange = (field: keyof TestEmailData, value: string) => {
    setTestEmail(prev => ({ ...prev, [field]: value }));
  };

  const saveSettings = async () => {
    try {
      setIsLoading(true);
      setMessage(null);

      const response = await apiClient.post('/settings/smtp/', settings);
      setMessage({ type: 'success', text: 'SMTP settings saved successfully!' });
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to save settings';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const testConnection = async () => {
    try {
      setIsTesting(true);
      setMessage(null);

      const response = await apiClient.post('/settings/smtp/test/', {
        ...settings,
        test_email: {
          recipients: testEmail.recipients.split(',').map(email => email.trim()),
          subject: testEmail.subject,
          message: testEmail.message
        }
      });

      setMessage({ type: 'success', text: `Test email sent successfully to ${testEmail.recipients}!` });
      setShowTestForm(false);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to send test email';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsTesting(false);
    }
  };

  const validateForm = () => {
    return settings.host && settings.port && settings.username && settings.from_email;
  };

  const validateTestForm = () => {
    return testEmail.recipients && testEmail.subject && testEmail.message;
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center mb-6">
        <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
          <Mail className="h-5 w-5 text-orange-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">SMTP Configuration</h2>
          <p className="text-sm text-gray-600">Configure email server settings for notifications</p>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
          message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
          'bg-blue-50 text-blue-700 border border-blue-200'
        }`}>
          {message.type === 'success' ? <Check className="h-4 w-4 mr-2" /> :
           message.type === 'error' ? <X className="h-4 w-4 mr-2" /> :
           <AlertCircle className="h-4 w-4 mr-2" />}
          {message.text}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); saveSettings(); }} className="space-y-4">
        {/* Server Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SMTP Host *
            </label>
            <input
              type="text"
              value={settings.host}
              onChange={(e) => handleSettingChange('host', e.target.value)}
              placeholder="smtp.gmail.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Port *
            </label>
            <input
              type="number"
              value={settings.port}
              onChange={(e) => handleSettingChange('port', parseInt(e.target.value))}
              placeholder="587"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
          </div>
        </div>

        {/* Authentication */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username *
            </label>
            <input
              type="text"
              value={settings.username}
              onChange={(e) => handleSettingChange('username', e.target.value)}
              placeholder="your-email@domain.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={settings.password}
                onChange={(e) => handleSettingChange('password', e.target.value)}
                placeholder="Enter password"
                className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
              <div className="absolute right-2 top-2 flex items-center space-x-1">
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="text-gray-400 hover:text-gray-600"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Security Options */}
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.use_tls}
              onChange={(e) => handleSettingChange('use_tls', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Use TLS</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.use_ssl}
              onChange={(e) => handleSettingChange('use_ssl', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Use SSL</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.skip_ssl_verification}
              onChange={(e) => handleSettingChange('skip_ssl_verification', e.target.checked)}
              className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="ml-2 text-sm text-gray-700">Skip SSL Certificate Verification</span>
          </label>
        </div>
        
        {/* SSL Verification Warning */}
        {settings.skip_ssl_verification && (
          <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-orange-800">
                  SSL Certificate Verification Disabled
                </h3>
                <div className="mt-1 text-sm text-orange-700">
                  <p>
                    This option allows connections to mail servers with self-signed or invalid certificates. 
                    Only enable this for trusted internal mail servers or development environments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* From Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Email *
            </label>
            <input
              type="email"
              value={settings.from_email}
              onChange={(e) => handleSettingChange('from_email', e.target.value)}
              placeholder="noreply@yourcompany.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Name
            </label>
            <input
              type="text"
              value={settings.from_name}
              onChange={(e) => handleSettingChange('from_name', e.target.value)}
              placeholder="KATA Lean 4.0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4 border-t">
          <button
            type="submit"
            disabled={isLoading || !validateForm()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Save Settings
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => setShowTestForm(!showTestForm)}
            disabled={!validateForm()}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Send className="h-4 w-4" />
            Test Email
          </button>
        </div>
      </form>

      {/* Test Email Form */}
      {showTestForm && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
          <h3 className="text-md font-medium text-gray-900 mb-4">Send Test Email</h3>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipients (comma-separated) *
              </label>
              <input
                type="text"
                value={testEmail.recipients}
                onChange={(e) => handleTestEmailChange('recipients', e.target.value)}
                placeholder="user1@example.com, user2@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject *
              </label>
              <input
                type="text"
                value={testEmail.subject}
                onChange={(e) => handleTestEmailChange('subject', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message *
              </label>
              <textarea
                value={testEmail.message}
                onChange={(e) => handleTestEmailChange('message', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={testConnection}
                disabled={isTesting || !validateTestForm()}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isTesting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send Test Email
                  </>
                )}
              </button>
              
              <button
                onClick={() => setShowTestForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="text-sm text-blue-700">
          <strong>Common SMTP Settings:</strong>
          <ul className="mt-1 space-y-1 text-xs">
            <li>• Gmail: smtp.gmail.com:587 (TLS) or smtp.gmail.com:465 (SSL)</li>
            <li>• Outlook: smtp-mail.outlook.com:587 (TLS)</li>
            <li>• Yahoo: smtp.mail.yahoo.com:587 (TLS)</li>
            <li>• Custom: Contact your email provider for SMTP settings</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SMTPConfiguration;
